import { Eye, Send, Pencil } from 'lucide-react'
import { fmtMoneda } from '@/lib/cd2bPricing'
import { resumenAnual } from '@/lib/cicloPago'
import { convencion, estiloBandera } from '@/lib/pais'
import CatalogoContador from '@/components/cd2b/CatalogoContador'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Alert, AlertDescription } from '@/components/ui/alert'
import TarjetaDemo, { IrAPaso } from './TarjetaDemo'
import TarjetaDatosPrueba from './TarjetaDatosPrueba'
import TarjetaProduccion from './TarjetaProduccion'
import TarjetaCobro from './TarjetaCobro'

// Lo que ocupa el sitio del asistente del catálogo fuera de la nube (B1). No se esconde sin más:
// el asesor que tiene delante a un cliente con catálogo propio necesita saber adónde mandarlo.
function NotaCatalogoEnEscritorio({ modalidad }) {
  return (
    <Card>
      <CardHeader><CardTitle className="text-h3">Catálogo de cuentas</CardTitle></CardHeader>
      <CardContent>
        <p className="text-body-sm text-neutral-600 dark:text-neutral-400">
          En instalación {modalidad === 'vps' ? 'en VPS propio' : 'local'} el catálogo del contador
          se trae desde el escritorio: <strong>Contabilidad → Traer mis datos → Plan de cuentas</strong>.
          Ahí acepta Excel (.xlsx) o CSV.
        </p>
      </CardContent>
    </Card>
  )
}
import { MODALIDADES } from './paquete'

function Dato({ label, paso, onIrAPaso, children }) {
  return (
    <div className="min-w-0">
      <div className="flex items-center gap-1.5">
        <p className="text-caption text-subtle">{label}</p>
        {paso && (
          <button type="button" onClick={() => onIrAPaso(paso)} aria-label={`Editar ${label.toLowerCase()} (paso ${paso})`}
            className="text-subtle hover:text-primary">
            <Pencil size={12} />
          </button>
        )}
      </div>
      <p className="text-body-sm text-neutral-900 dark:text-neutral-50 break-words">{children || '—'}</p>
    </div>
  )
}

/**
 * Paso 4 — Revisar y enviar. Es también la portada de una cotización que ya salió: lo que se hace
 * DESPUÉS de vender (demo, catálogo del contador, datos de prueba, producción) vive aquí, debajo
 * del resumen de lo vendido.
 */
export default function PasoRevisar({ c, onIrAPaso }) {
  const { form, setForm, esNueva, propuesta, quote, giroSel, tieneDemo } = c
  const anual = resumenAnual(form.ciclo, c.comision?.anual)
  const conv = convencion(form.pais)
  const nModulos = form.modulosSel.length

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader><CardTitle className="text-h3">Lo que se cotiza</CardTitle></CardHeader>
        <CardContent className="space-y-5">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 md:grid-cols-3">
            <Dato label="País" paso={1} onIrAPaso={onIrAPaso}>
              {form.pais ? (
                <span className="inline-flex items-center gap-1.5">
                  <span aria-hidden="true" className="inline-block h-3 w-4 rounded-sm border border-line" style={estiloBandera(form.pais)} />
                  {conv.nombre || form.pais}
                </span>
              ) : null}
            </Dato>
            <Dato label="Empresa" paso={1} onIrAPaso={onIrAPaso}>{form.empresaNombre}</Dato>
            <Dato label="Correo del cliente" paso={esNueva ? 1 : null} onIrAPaso={onIrAPaso}>{form.clienteEmail}</Dato>
            <Dato label="Giro" paso={1} onIrAPaso={onIrAPaso}>{giroSel?.label || form.giro}</Dato>
            <Dato label="Edición" paso={2} onIrAPaso={onIrAPaso}>{c.edicionLabel(form.edicion)}</Dato>
            <Dato label="Módulos" paso={2} onIrAPaso={onIrAPaso}>
              {nModulos ? `${nModulos} ${nModulos === 1 ? 'módulo' : 'módulos'}` : 'Todo lo de la edición'}
            </Dato>
            <Dato label="Asientos" paso={2} onIrAPaso={onIrAPaso}>
              {`${Number(form.seats.completo) || 0} completos · ${Number(form.seats.operativo) || 0} operativos`}
            </Dato>
            <Dato label="Modalidad" paso={3} onIrAPaso={onIrAPaso}>
              {MODALIDADES.find((m) => m.value === form.modalidad)?.label || form.modalidad}
            </Dato>
          </div>

          {/* Desglose EN VIVO — el mismo motor que factura Stripe y paga la comisión. */}
          <div className="rounded-lg border border-line bg-surface-muted p-4">
            <p className="text-body-sm font-semibold text-neutral-800 dark:text-neutral-200 mb-2">Resumen de precio</p>
            <div className="space-y-1">
              {quote.desglose.map((d, i) => (
                <div key={i} className="flex items-baseline justify-between gap-3 text-body-sm">
                  <span className="text-neutral-700 dark:text-neutral-300 min-w-0">
                    {d.concepto}{d.detalle ? <span className="text-caption text-neutral-400 dark:text-neutral-500"> · {d.detalle}</span> : null}
                  </span>
                  <span className="tabular-nums text-neutral-800 dark:text-neutral-200 shrink-0">{fmtMoneda(d.precio, form.moneda)}</span>
                </div>
              ))}
            </div>
            <div className="mt-3 pt-3 border-t border-line space-y-1">
              <div className="flex flex-wrap items-baseline justify-between gap-x-3">
                <span className="text-body-sm font-semibold text-neutral-800 dark:text-neutral-200">Total mensual</span>
                <span className="text-h3 font-bold tabular-nums text-primary-700 dark:text-primary-300">{fmtMoneda(quote.totalMensual, form.moneda)}</span>
              </div>
              {/* El cliente paga el año por adelantado: el total que cuenta es este, no el mensual. */}
              {anual && (
                <div className="pt-1">
                  <div className="flex flex-wrap items-baseline justify-between gap-x-3">
                    <span className="text-body-sm font-semibold text-neutral-800 dark:text-neutral-200">Pago anual (un año por adelantado)</span>
                    <span className="text-h3 font-bold tabular-nums text-primary-700 dark:text-primary-300">{fmtMoneda(anual.total, form.moneda)}</span>
                  </div>
                  {anual.ahorro > 0 && (
                    <p className="text-caption text-subtle">Ahorro frente a pagar 12 meses: {fmtMoneda(anual.ahorro, form.moneda)}</p>
                  )}
                </div>
              )}
              {quote.totalUnico > 0 && (
                <div className="pt-1">
                  <p className="text-caption text-neutral-500 dark:text-neutral-400 uppercase mb-0.5">Cargos únicos (primera factura)</p>
                  {(quote.cargosUnicos || []).map((cu, i) => (
                    <div key={i} className="flex items-baseline justify-between gap-3 text-body-sm">
                      <span className="min-w-0 text-neutral-600 dark:text-neutral-400">{cu.name || 'Cargo'}</span>
                      <span className="tabular-nums text-neutral-700 dark:text-neutral-300">{fmtMoneda(cu.amount, form.moneda)}</span>
                    </div>
                  ))}
                  <div className="flex items-baseline justify-between gap-3 text-body-sm font-medium">
                    <span className="text-neutral-700 dark:text-neutral-300">Total una vez</span>
                    <span className="tabular-nums text-neutral-800 dark:text-neutral-200">{fmtMoneda(quote.totalUnico, form.moneda)}</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => c.setPreviewOpen(true)}>
              <Eye size={15} className="mr-1.5" /> Vista previa para el cliente
            </Button>
            {/* Una ACEPTADA ya no se reenvía para aceptar: lo que falta es el pago (tarjeta «Cobro»). */}
            {!esNueva && !c.esPagada && propuesta?.estado !== 'aprobada' && (
              <Button variant="secondary" onClick={c.enviarAlDueno} disabled={c.enviando}>
                <Send size={15} className="mr-1.5" /> {c.enviando ? 'Enviando…' : 'Enviar al dueño por correo'}
              </Button>
            )}
          </div>
          {!esNueva && !c.esPagada && propuesta?.estado !== 'aprobada' && (
            <p className="text-caption text-subtle">
              El correo lleva lo que está <strong>guardado</strong>: si cambiaste algo, guarda antes de enviarlo.
            </p>
          )}
        </CardContent>
      </Card>

      {esNueva ? (
        <Alert>
          <AlertDescription>
            Guarda el borrador para poder crear la <strong>empresa demo</strong> que el dueño probará
            (elige Modalidad “Nube gestionada” en el <IrAPaso n={3} onIr={onIrAPaso}>paso 3</IrAPaso>).
          </AlertDescription>
        </Alert>
      ) : c.esPagada && !tieneDemo ? null : (
        // Ya pagada sin demo: crear una ahora no tiene sentido —la entrega crea la empresa real—.
        <TarjetaDemo c={c} onIrAPaso={onIrAPaso} />
      )}

      {/*
        EL CATÁLOGO DEL CONTADOR. Solo se pinta si el levantamiento dice que el cliente trae el
        suyo — el 90 % arranca con el estándar y no tiene nada que confirmar; enseñárselo sería
        ruido, y el ruido es lo que hace que se ignore el resto.

        Va JUSTO DEBAJO de «Crear empresa demo» y no en otra pantalla porque es lo que ese botón
        está esperando: mandar al asesor a buscarlo a otro sitio es cómo se abandonan las tareas.

        En la cotización que AÚN NO EXISTE va aquí también, en el último paso: la pregunta necesita
        que ya se haya hablado del negocio; ponerla primero es preguntar por el contador de una
        empresa que todavía no tiene nombre. La respuesta —y el archivo, si lo eligió— se sostienen
        en el formulario y viajan con el primer guardado, porque el catálogo se guarda DENTRO de la
        cotización.
      */}
      {/* `key` por la empresa: al crearse (demo o entrega) el asistente se vuelve a montar y
          relee su estado. Sin eso seguía diciendo «Todo listo: ya se puede crear la empresa» con
          la empresa ya creada, y no enseñaba si el catálogo había entrado. */}
      <div id="catalogo-contador" className="scroll-mt-24">
        {!c.entregaCatalogo ? <NotaCatalogoEnEscritorio modalidad={c.form.modalidad} /> : esNueva
          ? <CatalogoContador propuestaId={null} onDeclaracionCambia={c.setDeclaracionCatalogo} />
          : (
            <CatalogoContador
              key={`${propuesta?.demo?.tenantId || ''}|${propuesta?.instanciaNube?.tenantId || ''}|${propuesta?.estado || ''}`}
              propuestaId={c.id}
              onListoCambia={c.setCatalogoContador}
            />
          )}
      </div>

      {tieneDemo && <TarjetaDatosPrueba c={c} onIrAPaso={onIrAPaso} />}

      {propuesta?.estado === 'aprobada' && <TarjetaCobro c={c} />}
      {propuesta?.estado === 'pagada' && <TarjetaProduccion c={c} onIrAPaso={onIrAPaso} />}

      <Card>
        <CardContent className="pt-6">
          <Label htmlFor="cot-notas">Notas internas</Label>
          <Textarea id="cot-notas" rows={3} value={form.notas} onChange={(e) => setForm({ ...form, notas: e.target.value })} />
        </CardContent>
      </Card>
    </div>
  )
}
